/**
 * market-data-subscriptions — the ONE market-data fetch, shared app-wide.
 *
 * Every public page mounts the market hooks many times (inline `TokenSymbol`/`TagMention`,
 * cards, strips…). Historically each mount opened its OWN Firestore `onSnapshot`, so a page
 * held 5–30 duplicate listeners over the same few docs — each re-billed on every backend
 * rewrite for the tab's whole life, and per-browser (bypassing all caching).
 *
 * MARKET-ROUTE (the client half): those client listeners are gone. Instead one shared,
 * reference-counted poller hits the 60s-cached `GET /api/market/snapshot` and fans the four
 * feeds out to every subscriber. The route's CDN cache + its 60s in-memory TTL mean the whole
 * app costs ~one Firestore read per minute regardless of visitors. The poller shares ONE
 * `setInterval` across all mounts: opened on first subscribe, torn down (interval cleared)
 * when the last subscriber leaves; late subscribers are seeded from the cached value.
 *
 * Timestamp fields arrive as ISO strings (the route serializes Firestore Timestamps), which
 * is exactly the string shape the consuming hooks + components already expect.
 */
import { createSharedSubscription, type SharedSubscription } from './shared-firestore-subscription';
import type { FirestoreCryptoData } from './useFirestoreCrypto';
import type { MarketPulse, TokenInsights } from './useMarketAnalysis';
import type { FirestoreCollectionData } from './useFirestoreCollections';

/** The full payload of GET /api/market/snapshot (see src/lib/market-snapshot.ts). */
export interface MarketSnapshot {
  /** Latest `crypto_prices` doc, or null when the collection is empty. */
  crypto: FirestoreCryptoData | null;
  /** `market_analysis/pulse` doc, or null when absent. */
  pulse: MarketPulse | null;
  /** `market_analysis/insights` doc, or null when absent. */
  insights: TokenInsights | null;
  /** The whole `collections` collection, slug-keyed. */
  collections: FirestoreCollectionData[];
  /** ISO time the server assembled the snapshot. */
  fetchedAt: string;
}

const SNAPSHOT_URL = '/api/market/snapshot';
const POLL_INTERVAL_MS = 60_000;

/**
 * The ONE shared poller. Fetches the cached snapshot route immediately, then every 60s, and
 * emits to all subscribers. Reference-counted via `createSharedSubscription`: the interval is
 * created on first subscribe and cleared when the last subscriber leaves.
 */
export const marketSnapshotSubscription = createSharedSubscription<MarketSnapshot>(
  (emit, fail) => {
    // No polling during SSR — there is no window to fetch from and no consumer to paint.
    if (typeof window === 'undefined') return () => {};

    let cancelled = false;

    const tick = async () => {
      try {
        const res = await fetch(SNAPSHOT_URL);
        if (!res.ok) throw new Error(`market snapshot ${res.status}`);
        const data = (await res.json()) as MarketSnapshot;
        if (!cancelled) emit(data);
      } catch (err) {
        if (!cancelled) fail(err as Error);
      }
    };

    void tick();
    const timer = setInterval(() => void tick(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  },
);

/** Derive a slice of the shared snapshot as its own subscription (still ONE poller). */
function selectSnapshot<T>(select: (snap: MarketSnapshot) => T): SharedSubscription<T> {
  return {
    subscribe(onValue, onError) {
      return marketSnapshotSubscription.subscribe((snap) => onValue(select(snap)), onError);
    },
  };
}

/** Latest `crypto_prices` snapshot — one shared poll. */
export const cryptoPricesSubscription = selectSnapshot((snap) => snap.crypto);

/** `market_analysis/pulse` doc — null when absent (mirrors the old hook). */
export const marketPulseSubscription = selectSnapshot((snap) => snap.pulse);

/** `market_analysis/insights` doc — null when absent. */
export const marketInsightsSubscription = selectSnapshot((snap) => snap.insights);

/** The whole `collections` collection, slug-keyed. */
export const collectionsSubscription = selectSnapshot((snap) => snap.collections);
